/**
 * useHydrateReduxFromShare
 *
 * Populates Redux store from share API data.
 * This bridges React Query (which fetches share data) with Redux (which MobileTestDetails expects).
 *
 * Flow:
 * 1. ShareRoute fetches experience via useShareExperience (React Query)
 * 2. This hook dispatches Redux actions to populate store
 * 3. MobileTestDetails reads from Redux as usual
 */

import { useEffect } from "react";

import { AnalyzeTinybirdResourceResponseBodyType } from "@intelligems/ig-types/functions/analytics/analyze-resource-tinybird/analyze-resource-tinybird";
import { ExperienceFe } from "@intelligems/ig-types/fe-only/experiences";
import { GetExperienceResponseBodyType } from "@intelligems/ig-types/functions/experiences/get-experience";
import { GraphConfig } from "@intelligems/ig-types/schemas/analytics/custom-graphs";
import { filterDashboardDataTb } from "@src/api/analytics/dashboard-tinybird";
import { getVariationFeFromDto } from "@src/api/formatters/from-dto/variations";
import { useDispatch, useSelector } from "@src/store";
import { initCheckoutBlocksFromVariations } from "@src/store/slices/checkoutBlocks";
import {
  getExperienceAnalyticsFilters,
  setCompareGroups,
  setDashboardFilterByExperience,
} from "@src/store/slices/dashboardFilters";
import { initKeyMetrics } from "@src/store/slices/experienceMetrics/experienceMetrics";
import { initExperimentFromDto } from "@src/store/slices/experiences";
import { initVariations } from "@src/store/slices/variations";
import { UseQueryResult } from "@tanstack/react-query";

export const useHydrateReduxFromShare = (
  experienceData: GetExperienceResponseBodyType | undefined,
  experienceFe: ExperienceFe | undefined,
  analyticsData?: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[],
  graphConfigs?: GraphConfig[]
) => {
  const dispatch = useDispatch();
  const dashboardFilters = useSelector(getExperienceAnalyticsFilters);

  useEffect(() => {
    if (!experienceData?.experience || !experienceFe) {
      return;
    }

    const { experience } = experienceData;

    // Transform variations DTO to FE type for Redux store
    const variationsFe = getVariationFeFromDto(experience.variations || []);

    // Hydrate experience slice
    dispatch(
      initExperimentFromDto({
        experience,
        testFlowType: experience.type,
        defaultCurrency: experience.currency?.code,
        tab: "analytics", // Share views always show analytics
      })
    );

    // Hydrate variations slice (action internally transforms DTO to FE)
    dispatch(
      initVariations({
        variations: experience.variations,
      })
    );

    // Hydrate the checkoutBlocks slice — the authed loader does this via
    // useAndSetGenericExperimentFromUrl, which 401s under a share token. Without
    // it, getHasAnyCheckoutBlocks() is empty across the share UI (e.g. the User
    // Behavior filter can't render its "Viewed Checkout Block" option).
    dispatch(
      initCheckoutBlocksFromVariations({
        variations: experience.variations || [],
      })
    );

    // Hydrate experience metrics slice
    dispatch(
      initKeyMetrics({
        keyMetrics: experience.experienceKeyMetrics || [],
        hasTestPostPurchase: experience.testTypes.hasTestPostPurchase,
      })
    );

    // Hydrate dashboard filters with defaults
    // Note: primaryMetricId should come from experience.experienceKeyMetrics
    const primaryMetricId = experience.experienceKeyMetrics?.[0]?.id || "";

    // Slice isn't hydrated in share view — derive the per-variation every…
    // flags from the DTO variations. A conversion-stage default is fair only
    // when every variation carries a block. Guard the vacuous .every().
    // Any checkout-extension block type counts: they all emit the same
    // checkout_block_viewed event. postPurchaseUpsell is excluded to match the
    // authed path, where the slice keeps post-purchase blocks in their own
    // array — otherwise a post-purchase-only test defaults differently here.
    const variations = experience.variations ?? [];
    const everyVariationHasCheckoutBlock =
      variations.length > 0 &&
      variations.every((variation) =>
        variation.checkoutBlocks?.some(
          (block) => block.blockType !== "postPurchaseUpsell"
        )
      );
    const everyVariationHasPostPurchase =
      variations.length > 0 &&
      variations.every((variation) =>
        variation.checkoutBlocks?.some(
          (block) => block.blockType === "postPurchaseUpsell"
        )
      );

    dispatch(
      setDashboardFilterByExperience({
        experience: experienceFe,
        variations: variationsFe,
        dashboardData: undefined,
        maxZScore: "3",
        primaryMetricId,
        everyVariationHasCheckoutBlock,
        everyVariationHasPostPurchase,
      })
    );
  }, [experienceData, experienceFe, dispatch]);

  // Set compare groups after analytics data loads (like regular analytics)
  // This is needed because initial hydration doesn't have dashboard data yet
  // Only set if not already set - prevents overwriting user's manual swap selections
  useEffect(() => {
    if (!experienceData?.experience || !analyticsData || !graphConfigs) {
      return;
    }

    const { experience } = experienceData;

    // Only for experiments, not personalizations
    if (experience.category !== "experiment") {
      return;
    }

    // Only set compare groups if they haven't been set yet
    // This prevents overwriting user selections when data refreshes
    if (dashboardFilters.compareGroupA && dashboardFilters.compareGroupB) {
      return;
    }

    // Extract dashboard data using the same pattern as regular analytics
    const overviewConfig = graphConfigs.find(
      (c) => c.datasetId === "variation_overview"
    );
    if (!overviewConfig) {
      return;
    }

    const dashboardData = filterDashboardDataTb(
      analyticsData,
      "variation_overview",
      overviewConfig
    );
    if (!dashboardData) {
      return;
    }

    const variationsFe = getVariationFeFromDto(experience.variations || []);
    const primaryMetricId = experience.experienceKeyMetrics?.[0]?.id || "";

    dispatch(
      setCompareGroups({
        variations: variationsFe,
        dashboardData,
        primaryMetricId,
      })
    );
  }, [
    analyticsData,
    experienceData,
    graphConfigs,
    dashboardFilters.compareGroupA,
    dashboardFilters.compareGroupB,
    dispatch,
  ]);
};
